// src/utils/firebase/auth.js
import { auth } from './firebase.utils';
import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase.utils';
import { checkAndLogConfirmedEmailChange } from './activity';
import { authIsReady, setUser, setEmailVerificationPending, setUserCache, clearUserCache } from '../../store/user/user.action';
import { resetShowcaseState } from '../../store/showcase/showcase.action';
import { fetchUserFromFirestore } from './user';
import { syncPublicProfile } from './publicProfile';
import { getUserCache, saveUserCache } from '../cache/userCache';
import { clearAllUserData } from '../cache/clearAllUserData';

/**
 * Sets up Firebase Auth state listener
 * @returns {Function} Redux thunk function
 */
export const setupAuthListener = () => {
  return (dispatch) => {
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        // If user just confirmed an email change (clicked verification link), log it now
        if (user.email) {
          checkAndLogConfirmedEmailChange(user.uid, user.email);
        }

        // Get role from custom claims (secure, server-set)
        let role = "user";
        try {
          const tokenResult = await user.getIdTokenResult(true);
          role = tokenResult?.claims?.role || "user";
        } catch (err) {
          // Could not get role from token - use default
        }

        // Check if email is verified
        if (user.emailVerified) {
          dispatch(setEmailVerificationPending(false));
        } else {
          dispatch(setEmailVerificationPending(true));
        }
        
        // Set auth user immediately for fast UI (includes role from token)
        const authUserData = {
          uid: user.uid,
          email: user.email,
          role,
        };
        
        // Set auth ready immediately so app can proceed
        dispatch(authIsReady(authUserData));
        
        // Clear any previous user's Redux state to prevent data leakage
        dispatch(resetShowcaseState()); // Reset all showcase state
        
        // Try to get cached user data first
        const cachedUserData = getUserCache(user.uid);
        
        if (cachedUserData) {
          const completeUserData = {
            ...cachedUserData,
            uid: user.uid,
            email: user.email,
            role,
          };
          dispatch(setUser(completeUserData));
          dispatch(setUserCache(cachedUserData));
        } else {
          // Set basic auth data immediately
          dispatch(setUser(authUserData));
          
          // Fetch Firestore data in background
          try {
            const userDoc = await fetchUserFromFirestore(user.uid);
            
            if (userDoc) {
              const completeUserData = {
                ...userDoc,
                uid: user.uid,
                email: user.email,
                role,
              };
              dispatch(setUser(completeUserData));
              
              // Cache the data for next time
              saveUserCache(userDoc, user.uid);
              dispatch(setUserCache(userDoc));
            } else {
              // During account deletion, skip creating the user doc
              const isDeleting = typeof window !== 'undefined' && localStorage.getItem('deletingAccount') === 'true';
              if (!isDeleting) {
                // Create new user document
                const newUserData = {
                  email: user.email,
                  teamIndex: 0,
                  collectionCardCount: 0,
                  tokens: 0
                };
                await setDoc(doc(db, 'users', user.uid), newUserData);
                await syncPublicProfile(user.uid, newUserData);
                
                const completeUserData = {
                  ...newUserData,
                  uid: user.uid,
                  email: user.email,
                  role,
                };
                dispatch(setUser(completeUserData));
                
                // Cache the new data
                saveUserCache(newUserData, user.uid);
                dispatch(setUserCache(newUserData));
              }
            }
          } catch (error) {
            // Keep using auth data if Firestore fails
          }
        }
      } else {
        // User logged out - clear everything
        clearAllUserData(dispatch); // Clear ALL user data for security
        
        dispatch(authIsReady());
        dispatch(setUser(null));
        dispatch(setEmailVerificationPending(false));
        dispatch(clearUserCache());
      }
    });
  };
};

